import React from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import type { DialOpStateRow } from '../../../types/dialingOpportunity';
import {
  buildDialOpGridSx,
  DIAL_OP_PAGE_SIZE_OPTIONS,
  DialOpGridToolbar,
  HeaderWithTooltip,
} from './dialOpTableKit';

function formatLocalHour(hour?: number | null): string {
  if (hour === null || hour === undefined) return '—';
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${period}`;
}

interface StateBreakdownTableProps {
  rows: DialOpStateRow[];
  baselineWeeks?: number;
  driftRecentWeeks?: number;
  /** States currently active in the global filter -- can be more than one. */
  selectedStates?: string[];
  /** Toggles a single state in/out of the global filter's selection. */
  onStateToggle?: (state: string) => void;
}

export default function StateBreakdownTable({
  rows,
  baselineWeeks = 12,
  driftRecentWeeks = 4,
  selectedStates,
  onStateToggle,
}: StateBreakdownTableProps) {
  const theme = useTheme();

  const gridRows = React.useMemo(
    () =>
      rows.map((row, index) => ({
        id: row.state_cd ?? `row-${index}`,
        state_cd: row.state_cd ?? '—',
        first_contact_dials: row.first_contact_dials,
        actual_connects: row.actual_connects,
        missed_connects: row.missed_connects,
        median_dial_local_hr: row.median_dial_local_hr,
        best_hour: row.best_hour,
        best_hour_moved: row.best_hour_moved ?? false,
        recent_best_hour: row.recent_best_hour ?? null,
      })),
    [rows],
  );

  const clickable = Boolean(onStateToggle);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'state_cd',
        headerName: 'State',
        minWidth: 72,
        flex: 0.6,
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label="State" tooltip="The lead's state." />
        ),
      },
      {
        field: 'first_contact_dials',
        headerName: 'First calls',
        type: 'number',
        minWidth: 110,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="First calls"
            tooltip="How many new leads you called for the first time this period."
          />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined ? '—' : value.toLocaleString(),
      },
      {
        field: 'actual_connects',
        headerName: 'Reached',
        type: 'number',
        minWidth: 96,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="Reached"
            tooltip="First calls where the lead stayed on the line at least 90 seconds."
          />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined ? '—' : value.toLocaleString(),
      },
      {
        field: 'missed_connects',
        headerName: 'Missed',
        type: 'number',
        minWidth: 96,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="Missed"
            tooltip="About how many more conversations you could have had by calling at this state's best time."
          />
        ),
        valueFormatter: (value: number | null) =>
          value === null || value === undefined ? '—' : Number(value).toFixed(1),
      },
      {
        field: 'median_dial_local_hr',
        headerName: 'Median call time',
        type: 'number',
        minWidth: 132,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="Median call time"
            tooltip="The middle of your first-call times in the lead's local time. Half your calls were earlier, half later."
          />
        ),
        renderCell: (params: GridRenderCellParams) =>
          formatLocalHour(
            params.value === null || params.value === undefined
              ? null
              : Math.round(Number(params.value)),
          ),
      },
      {
        field: 'best_hour',
        headerName: 'Best time to call',
        type: 'number',
        minWidth: 172,
        flex: 1.2,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="Best time to call"
            tooltip={`The hour with the highest reach rate for this state, based on the last ${baselineWeeks} weeks.`}
          />
        ),
        renderCell: (params: GridRenderCellParams) => {
          const best = formatLocalHour(
            params.value === null || params.value === undefined ? null : Number(params.value),
          );
          const moved = params.row.best_hour_moved as boolean;
          const recent = params.row.recent_best_hour as number | null;
          if (!moved) return best;
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
              <span>{best}</span>
              <Tooltip
                arrow
                placement="top"
                title={`Best time shifted recently: the last ${driftRecentWeeks} weeks point to ${formatLocalHour(
                  recent,
                )}, vs. ${best} over the full ${baselineWeeks} weeks. Consider adjusting when this state is dialed.`}
              >
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<SwapVertRounded sx={{ fontSize: 13 }} />}
                  label={`→ ${formatLocalHour(recent)}`}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 11, fontWeight: 700 } }}
                />
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [baselineWeeks, driftRecentWeeks],
  );

  const gridSx = React.useMemo(
    () => buildDialOpGridSx(theme, { clickable }),
    [theme, clickable],
  );

  if (!rows.length) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            States losing the most conversations
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            icon={<HistoryRounded sx={{ fontSize: 15 }} />}
            label={`Best times: last ${baselineWeeks} weeks`}
            sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sorted by missed conversations. First calls, reached, and median call time are for the
          selected period. Best time to call comes from the last {baselineWeeks} weeks; a{' '}
          <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>
            → marker
          </Box>{' '}
          flags states whose best hour has shifted in the last {driftRecentWeeks} weeks.
          {clickable
            ? ' Click a state to add or remove it from the filter above, then hit Apply filters.'
            : ''}
        </Typography>
      </Box>

      <Box sx={{ width: '100%', minHeight: 420 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          disableRowSelectionOnClick
          sortingMode="client"
          paginationMode="client"
          pageSizeOptions={DIAL_OP_PAGE_SIZE_OPTIONS}
          localeText={{ toolbarQuickFilterPlaceholder: 'Search states…' }}
          initialState={{
            sorting: { sortModel: [{ field: 'missed_connects', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
          onRowClick={
            onStateToggle
              ? (params: GridRowParams) => {
                  const st = params.row.state_cd as string;
                  if (!st || st === '—') return;
                  onStateToggle(st);
                }
              : undefined
          }
          getRowClassName={(params) =>
            selectedStates?.includes(params.row.state_cd) ? 'dialop-row-selected' : ''
          }
          slots={{ toolbar: DialOpGridToolbar }}
          sx={gridSx}
          autoHeight
        />
      </Box>
    </Paper>
  );
}
